import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Save } from 'lucide-react';

/** ThemeTab — color palette + typography picker with live preview. */
export function ThemeTab({ theme, setTheme, saveTheme, saving }) {
  const colors = [
    { key: 'primary_color',    label: 'Primary' },
    { key: 'secondary_color',  label: 'Secondary' },
    { key: 'accent_color',     label: 'Accent' },
    { key: 'background_color', label: 'Background' },
    { key: 'card_color',       label: 'Card' },
    { key: 'text_color',       label: 'Text' },
    { key: 'muted_color',      label: 'Muted' },
    { key: 'danger_color',     label: 'Danger' },
  ];
  return (
    <Card className="bg-[#041A3B] border-white/10">
      <CardHeader>
        <CardTitle className="text-white">Theme Settings</CardTitle>
        <CardDescription className="text-[#A0B4CC]">Customize colors and typography</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h4 className="font-medium text-white mb-4">Colors</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {colors.map((c) => (
              <div key={c.key} className="space-y-2">
                <Label className="text-[#A0B4CC] text-sm">{c.label}</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={theme[c.key]}
                    onChange={(e) => setTheme({ ...theme, [c.key]: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <Input
                    value={theme[c.key]}
                    onChange={(e) => setTheme({ ...theme, [c.key]: e.target.value })}
                    className="bg-[#000B18] border-white/20 text-white text-sm flex-1"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 pt-6">
          <h4 className="font-medium text-white mb-4">Typography</h4>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-white">Heading Font</Label>
              <select
                value={theme.font_heading}
                onChange={(e) => setTheme({ ...theme, font_heading: e.target.value })}
                className="w-full p-2 rounded-md bg-[#000B18] border border-white/20 text-white"
              >
                <option value="Outfit">Outfit</option>
                <option value="Inter">Inter</option>
                <option value="Poppins">Poppins</option>
                <option value="Space Grotesk">Space Grotesk</option>
                <option value="Playfair Display">Playfair Display</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Body Font</Label>
              <select
                value={theme.font_body}
                onChange={(e) => setTheme({ ...theme, font_body: e.target.value })}
                className="w-full p-2 rounded-md bg-[#000B18] border border-white/20 text-white"
              >
                <option value="Manrope">Manrope</option>
                <option value="Inter">Inter</option>
                <option value="Open Sans">Open Sans</option>
                <option value="Roboto">Roboto</option>
                <option value="Work Sans">Work Sans</option>
              </select>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6">
          <h4 className="font-medium text-white mb-4">Preview</h4>
          <div
            className="p-6 rounded-lg border"
            style={{ backgroundColor: theme.background_color, borderColor: theme.primary_color + '30' }}
          >
            <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: theme.card_color }}>
              <h3 style={{ color: theme.text_color, fontFamily: theme.font_heading }} className="text-xl font-semibold mb-2">
                Card Title
              </h3>
              <p style={{ color: theme.muted_color, fontFamily: theme.font_body }}>
                This is how your content will look.
              </p>
            </div>
            <div className="flex gap-2">
              <button className="px-4 py-2 rounded-md font-medium" style={{ backgroundColor: theme.primary_color, color: theme.background_color }}>
                Primary Button
              </button>
              <button className="px-4 py-2 rounded-md font-medium" style={{ backgroundColor: theme.accent_color, color: theme.background_color }}>
                Accent Button
              </button>
            </div>
          </div>
        </div>

        <Button onClick={saveTheme} disabled={saving} className="btn-primary" data-testid="save-theme-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Theme
        </Button>
      </CardContent>
    </Card>
  );
}

export default ThemeTab;
